import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { SKIP_LOADER } from '../models/httpContextTokens';
import { liveRefresh } from '../helpers/live-refresh';
import { DeviceRealtimeService } from './device-realtime.service';

export type Presence = 'online' | 'offline' | 'unknown';

/** Times in Unix seconds, UTC, on the server clock. */
export interface DevicePresence {
  presence: Presence;
  lastSeen: number | null;
  /** Server time of the answer: offline durations are measured from here, not from a clock read by the template. */
  checkedAt: number;
  /** The edge gateway whose heartbeat reports the device; null when it has its own heartbeat. */
  gatewayId: string | null;
  gatewayName: string | null;
  /** The gateway itself is silent: nothing is known of the camera, lastSeen is the gateway's. */
  gatewayDown: boolean;
}

interface DevicePresenceItem {
  Id: string;
  LastSeen: number | null;
  Monitoring: number;
  Online: boolean;
  GatewayId: string | null;
  GatewayName: string | null;
}

interface DevicePresenceResponse {
  Devices: DevicePresenceItem[];
  Now: number;
}

interface PresenceSnapshot {
  byDevice: Map<string, DevicePresenceItem>;
  now: number;
}

const POLL_MS: number = 60_000;

export const UNKNOWN_PRESENCE: DevicePresence = { presence: 'unknown', lastSeen: null, checkedAt: 0, gatewayId: null, gatewayName: null, gatewayDown: false };

export type PresenceLookup = (deviceId: string) => DevicePresence;

// A camera is judged only while its gateway answers, as the backend does for
// the notifications: with the gateway silent the state is the gateway's.
function toPresence(snapshot: PresenceSnapshot | null, deviceId: string): DevicePresence {
  const item: DevicePresenceItem | undefined = snapshot?.byDevice.get(deviceId);

  if (!item) {
    return UNKNOWN_PRESENCE;
  }

  const base = { checkedAt: snapshot!.now, gatewayId: item.GatewayId, gatewayName: item.GatewayName };

  if (item.GatewayId) {
    const gateway: DevicePresenceItem | undefined = snapshot!.byDevice.get(item.GatewayId);

    if (!gateway?.Online) {
      return { ...base, presence: 'offline', lastSeen: gateway?.LastSeen ?? null, gatewayDown: true };
    }
  }

  return { ...base, presence: item.Online ? 'online' : 'offline', lastSeen: item.LastSeen, gatewayDown: false };
}

/** Phrase key and count: "Online", "Offline", "Offline for N min|h|d", or the same for the gateway. */
export function presenceLabel(state: DevicePresence): { key: string; count: number } {
  if (state.presence !== 'offline') {
    return { key: 'VIDEO_STATUS.ONLINE', count: 0 };
  }

  const prefix: string = state.gatewayDown ? 'VIDEO_STATUS.GATEWAY_OFFLINE' : 'VIDEO_STATUS.OFFLINE';

  if (state.lastSeen === null) {
    return { key: prefix, count: 0 };
  }

  const seconds: number = Math.max(0, state.checkedAt - state.lastSeen);

  if (seconds < 3600) {
    return { key: `${prefix}_MIN`, count: Math.max(1, Math.floor(seconds / 60)) };
  }

  if (seconds < 86400) {
    return { key: `${prefix}_HOURS`, count: Math.floor(seconds / 3600) };
  }

  return { key: `${prefix}_DAYS`, count: Math.floor(seconds / 86400) };
}

/**
 * Presence from the heartbeats of the monitored devices (Monitoring property):
 * totems, edge gateways and the cameras they reach. One shared reading for the
 * whole app while anything watches, refreshed as `liveRefresh` says; a device
 * that is not monitored is unknown.
 */
@Injectable({ providedIn: 'root' })
export class DevicePresenceService {
  private readonly http = inject(HttpClient);
  private readonly realtime = inject(DeviceRealtimeService);

  private readonly snapshot$: Observable<PresenceSnapshot | null> = liveRefresh(this.realtime, POLL_MS).pipe(
    startWith(undefined),
    switchMap(() => this.http.get<DevicePresenceResponse>('uv.device.presence.api', { context: new HttpContext().set(SKIP_LOADER, true) })
      .pipe(
        map(res => ({ byDevice: new Map((res?.Devices ?? []).map(d => [d.Id, d] as const)), now: res?.Now ?? 0 })),
        catchError(() => of(null))
      )),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /** Every device at once, for pages that list many; emits at each poll. */
  public watchAll(): Observable<PresenceLookup> {
    return this.snapshot$.pipe(map(snapshot => (deviceId: string) => toPresence(snapshot, deviceId)));
  }

  public watch(deviceId: string | null | undefined): Observable<DevicePresence> {
    if (!deviceId) {
      return of(UNKNOWN_PRESENCE);
    }

    return this.snapshot$.pipe(
      map(snapshot => toPresence(snapshot, deviceId)),
      // Offline passes every poll: "offline for N min" has to grow.
      distinctUntilChanged((a, b) => a.presence !== 'offline' && a.presence === b.presence && a.lastSeen === b.lastSeen && a.gatewayDown === b.gatewayDown)
    );
  }
}
